import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { CasingSizeFiltersResponse, PaginatedCasingSizeInfo, Pagination } from '../../models/response.model';
import { map, Observable } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { CasingSize } from '../../models/casing-size.model';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';
import { ColumnPreferencesService } from '../../../../shared/services/column-preferences.service';
import { ColumnPreferencesHelperService } from '../../../../shared/services/column-preferences-helper.service';

@Component({
  selector: 'app-casing-size',
  templateUrl: './casing-size.component.html',
  styleUrl: './casing-size.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingSizeComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<CasingSize>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);
  public columnPrefService = inject(ColumnPreferencesService);
  private dialog = inject(MatDialog);
  public columnPrefsHelper = inject(ColumnPreferencesHelperService);

  sizeList: string[] = [];
  descriptionList: string[] = [];
  constructionList: string[] = [];

  // Table dropdown lists
  dealerRetreadNoList: string[] = [];

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  casingSizes: CasingSize[] = [];
  // Store original data to detect changes accurately
  private originalCasingSizes: CasingSize[] = [];
  plants: string[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  pagination: Pagination;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  productSaveDisabled = true;
  saveEnabled = false;
  isModified = false;

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-CasingSize-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  tableConfig: TableConfig<CasingSize> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {}
  };
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;
  columnNames: Record<string, string> = {};
  private canonicalColumns: Record<string, any> = {};
  columnKeys: string[] = [];
  columnOrder: string[] = [];
  columnVisibility: Record<string, boolean> = {};

  exportDataFetcher: () => Observable<ExportResponse> = () =>
    this.productDataService.getCasingSizes(this.filterFormBackend.value, 1, 10000).pipe(
      map((response: any) => ({
        data: (response.data.casingSizes as CasingSize[]).map((item) => ({
          casingSize: item.casingSize,
          description: item.description,
          casingConstruction: item.casingConstruction,
          minCircum: item.minCircum,
          maxCircum: item.maxCircum,
          minBuildCircum: item.minBuildCircum,
          maxBuildCircum: item.maxBuildCircum,
          stockCasingPartNumber: item.stockCasingPartNumber,
          ccPartNumber: item.ccPartNumber,
          cpRejectPartNumber: item.cpRejectPartNumber,
          tier: item.tier
        }))
      }))
    );

  fetcher: PaginatorFetcher<CasingSize> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();
    return this.productDataService.getCasingSizes({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((response: any) => {
        this.casingSizes = response.data.casingSizes || [];
        this.pagination = response.data.pagination;
        this.updateDisplayedRowsForm(this.casingSizes);
        return { items: this.casingSizes, count: this.pagination.totalRecords };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      size: [[]],
      description: [[]],
      construction: [[]]
    });
    this.filterFormBackend = this.fb.group({
      size: [[]],
      description: [[]],
      construction: [[]]
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.productDataService.getCasingSizesFilters().subscribe((response: CasingSizeFiltersResponse) => {
      this.sizeList = response.data.casingSizes || [];
      this.descriptionList = response.data.sizeDescriptions || [];
      this.constructionList = response.data.casingConstructions || [];
      this.filterForm.patchValue({
        size: [],
        description: []
      });
    });
    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
    });
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.isModified = this.displayedRowsForm.dirty;
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.columnNames = {
        casingSize: this.translationService.translate('casingSizeText'),
        description: this.translationService.translate('descriptionText'),
        casingConstruction: this.translationService.translate('constructionText'),
        minCircum: this.translationService.translate('minBuffText'),
        maxCircum: this.translationService.translate('maxBuffText'),
        minBuildCircum: this.translationService.translate('minBuildText'),
        maxBuildCircum: this.translationService.translate('maxBuildText'),
        stockCasingPartNumber: this.translationService.translate('stockCasingText'),
        ccPartNumber: this.translationService.translate('ccPartText'),
        cpRejectPartNumber: this.translationService.translate('cpRejectText'),
        tier: this.translationService.translate('tierText')
      };

      // build canonical columns (used as source of truth) and apply saved preferences
      this.canonicalColumns = {
        casingSize: { cell: (row) => row.casingSize, header: this.translationService.translate('casingSizeText') },
        description: { cell: (row) => row.description, header: this.translationService.translate('descriptionText') },
        casingConstruction: { cell: (row) => row.casingConstruction, header: this.translationService.translate('constructionText') },
        minCircum: { cell: (row) => row.minCircum, header: this.translationService.translate('minBuffText') },
        maxCircum: { cell: (row) => row.maxCircum, header: this.translationService.translate('maxBuffText') },
        minBuildCircum: { cell: (row) => row.minBuildCircum, header: this.translationService.translate('minBuildText') },
        maxBuildCircum: { cell: (row) => row.maxBuildCircum, header: this.translationService.translate('maxBuildText') },
        stockCasingPartNumber: { cell: 'stockCasingDropdown', header: this.translationService.translate('stockCasingText') },
        ccPartNumber: { cell: 'ccPartDropdown', header: this.translationService.translate('ccPartText') },
        cpRejectPartNumber: { cell: 'cpRejectDropdown', header: this.translationService.translate('cpRejectText') },
        tier: { cell: 'tier', header: this.translationService.translate('tierText') }
      };

      // apply user preferences (localStorage) and initialize tableConfig.columns
      const init = this.columnPrefsHelper.initialize('casing-size', this.canonicalColumns);
      this.tableConfig.columns = init.tableColumns;
      this.columnNames = init.columnNames;
      this.columnKeys = init.columnKeys;
      this.columnOrder = init.columnOrder;
      this.columnVisibility = init.columnVisibility;
      this.cdr.markForCheck();
    });
    this.updateDropdownLists();
  }

  updateDropdownLists(): void {
    // TODO: Populate dropdown lists for table columns from products or backend
    this.dealerRetreadNoList.push(...this.store.selectSnapshot(ProductsState.products).rollPartNumbers);
  }

  // Toggle visibility for a column and persist preferences (delegates to helper)
  toggleColumn(key: string): void {
    const result = this.columnPrefsHelper.toggleAndSave('casing-size', key, this.canonicalColumns, this.columnOrder, this.columnVisibility);
    this.tableConfig.columns = result.tableColumns;
    this.columnVisibility = result.columnVisibility;
    this.cdr.markForCheck();
  }

  createRowForm(row: CasingSize): FormGroup {
    return this.fb.group({
      casingSizePk: [row.casingSizePk],
      stockCasingPartNumber: [row.stockCasingPartNumber],
      ccPartNumber: [row.ccPartNumber],
      cpRejectPartNumber: [row.cpRejectPartNumber],
      tier: [row.tier],
      casingSize: [row.casingSize],
      description: [row.description],
      casingConstruction: [row.casingConstruction],
      minCircum: [row.minCircum],
      maxCircum: [row.maxCircum],
      minBuildCircum: [row.minBuildCircum],
      maxBuildCircum: [row.maxBuildCircum]
    });
  }

  private updateDisplayedRowsForm(rows: CasingSize[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
    // Store deep copy of original data for accurate change detection
    this.originalCasingSizes = JSON.parse(JSON.stringify(rows));
    // Mark form as pristine to reset dirty state
    this.displayedRowsForm.markAsPristine();
    this.displayedRowsForm.markAsUntouched();
    this.isModified = false;
  }

  getControl(item: CasingSize, controlName: keyof CasingSize): FormControl {
    const index = this.casingSizes.findIndex((row) => row.casingSize === item.casingSize);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  clearFilters(): void {
    this.filterForm.reset({
      size: [],
      description: [],
      construction: []
    });
    // Also reset the displayed rows form to pristine state
    this.updateDisplayedRowsForm(this.casingSizes);
  }

  save(): void {
    if (!this.saveEnabled) {
      return;
    }
    const modifiedRows = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.casingSizes.length; i++) {
      const original = this.originalCasingSizes[i] || this.casingSizes[i];
      const current = formArray.at(i).value;

      // Compare each property, normalizing null/undefined/empty string to the same value
      const isModified = Object.keys(current).some((key) => {
        const origVal = this.normalizeValue(original[key]);
        const currVal = this.normalizeValue(current[key]);
        return origVal !== currVal;
      });

      if (isModified) {
        modifiedRows.push({ ...current, casingSizePk: original.casingSizePk });
      }
    }
    if (modifiedRows.length > 0) {
      const payload: PaginatedCasingSizeInfo = {
        casingSizes: modifiedRows,
        pagination: this.pagination
      };
      this.productDataService.updateCasingSizes(payload).subscribe({
        next: () => {
          // Sync in-memory list with saved form values and update baseline
          const formArray = this.displayedRowsForm.get('rows') as FormArray;
          this.casingSizes = this.casingSizes.map((original, i) => ({
            ...original,
            ...formArray.at(i).value
          }));
          // Update original data to reflect the saved state
          this.originalCasingSizes = JSON.parse(JSON.stringify(this.casingSizes));

          this.displayedRowsForm.markAsPristine();
          this.displayedRowsForm.markAsUntouched();
          this.isModified = false;

          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('camSaveSuccessText')
          } as Alert);
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('criticalErrorAlertText')
          } as Alert);
        }
      });
    } else {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noModifiedText')
      } as Alert);
    }
  }

  /**
   * Normalize values for comparison (treat null, undefined, and empty string as equivalent)
   */
  private normalizeValue(value: any): any {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return value;
  }

  generateFilterForm(): void {
    const filterSize = this.filterForm.get('size').value;
    const filterDescription = this.filterForm.get('description').value;
    this.filterFormBackend.patchValue({
      size: filterSize.length > 0 && filterSize.length == this.sizeList.length ? [] : filterSize,
      description: filterDescription.length > 0 && filterDescription.length == this.descriptionList.length ? [] : filterDescription,
      construction: this.filterForm.get('construction').value
    });
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        // Reset the form to original state
        this.updateDisplayedRowsForm(this.casingSizes);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      }
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.checkIfModified()) {
      $event.returnValue = true;
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.checkIfModified()) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  checkIfModified() {
    const modifiedRows = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.casingSizes.length; i++) {
      const original = this.originalCasingSizes[i] || this.casingSizes[i];
      const current = formArray.at(i).value;

      // Compare each property, normalizing null/undefined/empty string to the same value
      const isModified = Object.keys(current).some((key) => {
        const origVal = this.normalizeValue(original[key]);
        const currVal = this.normalizeValue(current[key]);
        return origVal !== currVal;
      });

      if (isModified) {
        modifiedRows.push({ ...current, casingSizePk: original.casingSizePk });
      }
    }
    return modifiedRows.length > 0;
  }
}
